package scenes

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import domain.PlayGameMode
import engine.model.MatchConfig

// ── Scene names ───────────────────────────────────────────────────────────────

enum class SceneName { MENU, PLAY, GAME, CUSTOM_GAME, STORY, SETTINGS }

// ── Navigation options ────────────────────────────────────────────────────────

/**
 * Optional payload passed alongside a navigation action.
 *
 * @param mode       The [PlayGameMode] to use when navigating to [SceneName.GAME].
 * @param matchConfig A fully-configured [MatchConfig] for custom game sessions.
 */
data class NavigateOptions(
    val mode: PlayGameMode? = null,
    val matchConfig: MatchConfig? = null,
)

// ── Navigate typealias ────────────────────────────────────────────────────────

/**
 * Callback type passed to every scene so they can trigger navigation without
 * directly mutating shared state.
 *
 * Usage: `navigate(SceneName.GAME, NavigateOptions(mode = PlayGameMode.SPRINT))`
 */
typealias Navigate = (SceneName, NavigateOptions?) -> Unit

// ── SceneHost ─────────────────────────────────────────────────────────────────

/**
 * Root scene container.
 *
 * Maintains the current [SceneName] and optional [NavigateOptions] as
 * [androidx.compose.runtime.MutableState]. Renders the active scene inside an
 * [AnimatedContent] block with [fadeIn]/[fadeOut] transitions so scene changes
 * are visually smooth.
 *
 * Each scene receives a [Navigate] callback so it can trigger navigation
 * without importing or mutating this composable's state directly.
 */
@Composable
fun SceneHost() {
    var current by remember { mutableStateOf(SceneName.MENU) }
    var options by remember { mutableStateOf<NavigateOptions?>(null) }

    val navigate: Navigate = { name, opts ->
        options = opts
        current = name
    }

    AnimatedContent(
        targetState = current,
        transitionSpec = { fadeIn() togetherWith fadeOut() },
    ) { scene ->
        when (scene) {
            SceneName.MENU        -> MenuScene(navigate)
            SceneName.PLAY        -> PlayScene(navigate)
            SceneName.GAME        -> GameScene(navigate, options?.mode, options?.matchConfig)
            SceneName.CUSTOM_GAME -> CustomGameScene(navigate)
            SceneName.STORY       -> StoryScene(navigate)
            SceneName.SETTINGS    -> SettingsScene(navigate)
        }
    }
}


